'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Rook opening
//***********************************************************************************
//***********************************************************************************

import { fh_add, fh_clone, fh_cross, fh_extruded_polygon, fh_matrix, fh_mul, fh_normalize, fh_polygon, fh_solid, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_camera } from '../svg/cn_camera';
import { cn_add, cn_box, cn_clone, cn_dot, cn_mul, cn_normal, cn_polar, cn_sub } from '../utils/cn_utilities';
import { cn_element } from './cn_element';
import { cn_roof } from './cn_roof';
import { cn_facing_group } from './cn_facing';
import { cn_roof_opening_type } from './cn_roof_opening_type';
import { cn_constant, CN_FACING_SUPPORTS, CN_OPENING_FACING_CLOSING, CN_OPENING_FACING_MAIN, CN_OPENING_FACING_OPENING, CN_OPENING_FACING_SASH } from './cn_model_constants';

export class cn_roof_opening extends cn_element {
    //***********************************************************************************
    /**
     * Constructor
     * @param {cn_roof_opening_type} roof_opening_type
     * @param {cn_roof} roof
     */
    constructor(roof_opening_type, roof) {
        super(roof);
        this.class_name = 'cn_roof_opening';

        //*** Model data
        this.width = -1;
        this.length = -1;
        this.opening_type = roof_opening_type
        this.position = [0, 0];
        this.orientation = 0;
        this.facings = [[null, null], [null, null]];
        this.substrates = [[null, null], [null, null]];
        this.similar_facings = true;

        //*** volatile data */
        this.slab = null;
        this.orientation_offset = 0;
        this.vertices = [];
    }

    //***********************************************************************************
    /**
     * serialize
     * @returns {object}
     */
    serialize() {
        var json = this.serialize_element();
        json.width = this.width;
        json.length = this.length;
        json.opening_type = this.opening_type.ID;
        json.position = cn_clone(this.position);
        json.orientation = this.orientation;
        json.facings = this.facings.map(fl => fl.map(f => f ? f.ID : null));
        json.substrates = this.substrates.map(fl => fl.map(f => f ? f.ID : null));
        json.similar_facings = this.similar_facings;
        return json;
    }

    //***********************************************************************************
    /**
     * Unserialize
     * @param {object} json
     * @param {cn_roof} roof
     * @returns {cn_roof_opening}
     */
    static unserialize(json, roof) {
        if (typeof (json.ID) != 'string') return null;
        if (typeof (json.opening_type) != 'string') return null;
        if (typeof (json.position) != 'object') return null;
        if (typeof (json.orientation) != 'number') return null;

        var rot = roof.building.get_element_type(json.opening_type);
        if (rot == null) {
            console.error('Error reading roof opening : roof opening type not found');
            return null;
        }
        var ro = new cn_roof_opening(rot, roof);
        ro.unserialize_element(json);

        if (typeof (json.width) == 'number')
            ro.width = json.width;

        if (typeof (json.length) == 'number')
            ro.length = json.length;

        ro.position = cn_clone(json.position);
        ro.orientation = json.orientation;

        cn_facing_group.unserialize_facings(ro.facings, json, roof.building);
        cn_facing_group.unserialize_substrates(ro.facings, json.substrtes, roof.building.get_mmm_compositions());

        roof.openings.push(ro);
        return ro;
    }

    /**
     * Retruns a readable ID
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        let name = "To" + storey.storey_index + "B";
        name += (this.parent.openings.indexOf(this) + 1);
        return name;
    }

    /**
     * returns the opening width
     * @returns {number}
     */
    get_width() {
        if (this.width > 0) return this.width;
        return this.opening_type.width;
    }

    /**
     * returns the opening height
     * @returns {number}
     */
    get_length() {
        if (this.length > 0) return this.length;
        return this.opening_type.length;
    }

    /**
     * returns derivate opening type
     * @returns {cn_roof_opening_type}
     */
    get_opening_type() {
        if (this.width > 0 || this.length > 0)
            return this.opening_type.get_derivate(this.get_width(), this.get_length());
        return this.opening_type;
    }

    //***********************************************************************************
    /**
     * Draw
     * @param {cn_camera} camera
     * @param {string[]} add_classes
     * @returns
     */
    draw(camera, add_classes = [], fill = '') {
        var html = '';
        if (this.opening_type == null) return html;

        var mouseover = (add_classes.indexOf('mouseover') >= 0);
        var selected = (add_classes.indexOf('selected') >= 0);

        var p = camera.world_to_screen(this.position);
        var sz = [this.get_width() * camera.world_to_screen_scale, this.get_length() * camera.world_to_screen_scale];

        if (this.slab == null) html += '<g opacity=\'0.3\'>';


        html += '<g transform=\'translate(' + p[0] + ',' + p[1] + ') rotate(' + (-this.orientation) + ')  \'>';
        html += '<rect x=\'' + (-sz[0] * 0.5) + '\' y=\'' + (-sz[1] * 0.5) + '\' width=\'' + sz[0] + '\' height=\'' + sz[1] + '\' ';
        html += ' style=\'stroke:black; stroke-width: 1px; fill:white\' ';
        if (mouseover)
            html += 'filter=\'url(#mouseover_shadow)\'';
        else if (selected)
            html += 'filter=\'url(#selection_shadow)\'';
        else
            html += 'filter=\'url(#object_shadow)\'';
        html += '/>';

        html += this.get_opening_type().draw_svg(sz[0], sz[1], fill);

        if (mouseover || selected)
            html += '<rect class=\'object_contour ' + add_classes.join(' ') + '\' x=\'' + (-sz[0] * 0.5) + '\' y=\'' + (-sz[1] * 0.5) + '\' width=\'' + sz[0] + '\' height=\'' + sz[1] + '\' />';

        html += '</g>';

        if (this.slab == null) html += '</g>';

        return html;
    }

    //***********************************************************************************
    /**
     * Draw contour
     * @param {cn_camera} camera
     * @param {string[]} add_classes
     * @returns {string}
     */
    draw_contour(camera, add_classes) {
        var html = '';

        var p = camera.world_to_screen(this.position);
        var sz = [this.get_width() * camera.world_to_screen_scale, this.get_length() * camera.world_to_screen_scale];

        html += '<g transform=\'translate(' + p[0] + ',' + p[1] + ') rotate(' + (-this.orientation) + ') translate(' + (-0.5 * sz[0]) + ',' + (-0.5 * sz[1]) + ') \'>';
        html += '<rect class=\'object_contour + ' + add_classes.join(' ') + '\' x=\'0\' y=\'0\' width=\'' + sz[0] + '\' height=\'' + sz[1] + '\' />';
        html += '</g>';
        return html;
    }

    //***********************************************************************************
    /**
     * update
     */
    update() {
        this.slab = this.parent.find_slab(this.position);
        if (this.slab && this.slab.slope > 0) {
            var pol = cn_polar(this.slab.slope_direction);
            this.orientation_offset = pol[1];
        } else
            this.orientation_offset = 0;

        this.vertices = [];
        var dx = [Math.cos(this.orientation * Math.PI / 180), Math.sin(this.orientation * Math.PI / 180)];
        var dy = cn_normal(dx);
        dx = cn_mul(dx, this.get_width() * 0.5);
        dy = cn_mul(dy, this.get_length() * 0.5);
        this.vertices.push(cn_sub(this.position, cn_add(dx, dy)));
        this.vertices.push(cn_sub(this.position, cn_sub(dx, dy)));
        this.vertices.push(cn_add(this.position, cn_add(dx, dy)));
        this.vertices.push(cn_add(this.position, cn_sub(dx, dy)));
    }

    //***********************************************************************************
    /**
     * Contains
     * @param {number[]} point
     * @param {number} tolerance
     * @returns {boolean}
     */
    contains(point, tolerance = 0) {
        var d = cn_sub(point, this.position);
        var sz0 = this.get_width() + 2 * tolerance;
        var sz1 = this.get_length() + 2 * tolerance;
        if (cn_dot(d, d) > sz0 * sz0 + sz1 * sz1) return false;
        var dx = [Math.cos(this.orientation * Math.PI / 180), Math.sin(this.orientation * Math.PI / 180)];
        if (Math.abs(cn_dot(d, dx)) > sz0 * 0.5) return false;
        var dy = cn_normal(dx);
        if (Math.abs(cn_dot(d, dy)) > sz1 * 0.5) return false;
        return true;
    }

    //***********************************************************************************
    /**
     * Returns true if completelu contained in box
     * @param {cn_box} box
     */
    contained_by_box(box) {
        if (!box.contains_point(this.position)) return false;

        var dx = [Math.cos(this.orientation * Math.PI / 180), Math.sin(this.orientation * Math.PI / 180)];
        var dy = cn_normal(dx);
        dx = cn_mul(dx, 0.5 * this.get_width());
        dy = cn_mul(dy, 0.5 * this.get_length());
        var p = cn_sub(this.position, cn_add(dx, dy));
        if (!box.contains_point(p)) return false;
        p = cn_sub(this.position, cn_sub(dx, dy));
        if (!box.contains_point(p)) return false;
        p = cn_add(this.position, cn_add(dx, dy));
        if (!box.contains_point(p)) return false;
        p = cn_add(this.position, cn_sub(dx, dy));
        if (!box.contains_point(p)) return false;
        return true;
    }

    //***********************************************************************************
    /**
     * Builds 3D matrix
     * @param {number} h : actual height at level 0
     * @returns {fh_matrix}
     */
    build_3d_matrix(h) {
        var matrix = new fh_matrix();
        if (this.slab == null) return null;

        var pos = cn_clone(this.position);
        h += this.slab.compute_height(pos);
        pos.push(h);
        pos = fh_add(pos, fh_mul(this.slab.normal, this.slab.slab_type.thickness));

        var dz = fh_clone(this.slab.normal);
        var a = this.orientation * Math.PI / 180;
        var dx = [Math.cos(a), Math.sin(a), 0];
        var dy = fh_cross(dz, dx);
        fh_normalize(dy);
        dx = fh_cross(dy, this.slab.normal);
        fh_normalize(dx);
        matrix.load_axis(pos, dx, dy, dz);
        return matrix;
    }

    //***********************************************************************************
    /**
     * Builds piercing as extruded polyong through local matrix
     * @returns {fh_extruded_polygon}
     */
    build_local_piercing() {
        if (this.slab == null) return null;
        return this.get_opening_type().build_piercing_extrusion([0, 0, -this.slab.slab_type.thickness - 0.01], [1, 0, 0], [0, 1, 0], [0, 0, 1], this.slab.slab_type.thickness + 0.02);
    }

    //***********************************************************************************
    /**
     * Builds 3D piercing as a solid.
     * @param {number} h
     * @returns {fh_solid}
     */
    build_piercing(h) {
        if (this.slab == null) return null;
        var pos = cn_clone(this.position);
        pos.push(this.slab.compute_height(pos) + h);
        var a = this.orientation * Math.PI / 180;
        var dx = [Math.cos(a), Math.sin(a), 0];
        var dy = fh_cross(this.slab.normal, dx);
        fh_normalize(dy);
        dx = fh_cross(dy, this.slab.normal);
        fh_normalize(dx);
        return this.get_opening_type().build_piercing_solid(fh_sub(pos, fh_mul(this.slab.normal, 0.01)), dx, dy, this.slab.normal, this.slab.slab_type.thickness + 0.02);
    }

    //***********************************************************************************
    /**
     * Builds 3D piercing as a solid.
     * @param {number} h
     * @returns {fh_polygon}
     */
    build_piercing_polygon(h) {
        if (this.slab == null) return null;
        var pos = cn_clone(this.position);
        pos.push(this.slab.compute_height(pos) + h);
        var a = this.orientation * Math.PI / 180;
        var dx = [Math.cos(a), Math.sin(a), 0];
        var dy = fh_cross(this.slab.normal, dx);
        fh_normalize(dy);
        dx = fh_cross(dy, this.slab.normal);
        fh_normalize(dx);
        return this.get_opening_type().build_piercing_polygon(fh_sub(pos, fh_mul(this.slab.normal, 0.01)), dx, dy, this.slab.normal);
    }

    //***********************************************************************************
    /**
     * Builds 3D opening, as a polygon at the interior roof.
     * @param {number} h
     * @returns {fh_polygon}
     */
    build_3d_opening(h) {
        if (this.slab == null) return null;
        var pos = cn_clone(this.position);
        pos.push(this.slab.compute_height(pos) + h);
        var a = this.orientation * Math.PI / 180;
        var dx = [Math.cos(a), Math.sin(a), 0];
        var dy = fh_cross(this.slab.normal, dx);
        fh_normalize(dy);
        dx = fh_cross(dy, this.slab.normal);
        fh_normalize(dx);
        var pg = new fh_polygon(pos, this.slab.normal);
        var contour = [];
        pos = fh_sub(pos, fh_mul(dx, 0.5 * this.get_width()));
        pos = fh_sub(pos, fh_mul(dy, 0.5 * this.get_length()));
        contour.push(pos);
        pos = fh_add(pos, fh_mul(dx, this.get_width()));
        contour.push(pos);
        pos = fh_add(pos, fh_mul(dy, this.get_length()));
        contour.push(pos);
        pos = fh_sub(pos, fh_mul(dx, this.get_width()));
        contour.push(pos);
        pg.add_contour(contour);
        return pg;
    }

    //***********************************************************************************
    /**
     * Performs a rotation operation
     * @param {number[]} center : center of ritation
     * @param {number} angle : rotation angle, in radians
     * @param {function} rotation_function : fnction that transforms a 2D point
     */
    perform_rotation(center, angle, rotation_function) {
        rotation_function(this.position);
        this.orientation += angle * 180 / Math.PI;
    }

    //***********************************************************************************
    /**
     * Vertex operation : transform all vertices
     * @param {function} operation : vertex operator
     */
    vertex_operation(operation) {
        operation(this.position);
    }

    //***********************************************************************************
    /**
     * flip operation : transform all vertices
     * @param {number[]} center : center of flip
     * @param {boolean} horizontal : true for horizontal flip, vertical otherwise
     * @param {function} operation : vertex operator
     */
    perform_flip(center, horizontal, operation) {
        operation(this.position);
        this.flipped = !this.flipped;
        if (horizontal)
            this.orientation = 180 - this.orientation;
        else
            this.orientation = -this.orientation;
    }

    //***********************************************************************************
    /**
     * Returns bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        var box = new cn_box();
        var dx = [Math.cos(this.orientation * Math.PI / 180), Math.sin(this.orientation * Math.PI / 180)];
        var dy = cn_normal(dx);
        dx = cn_mul(dx, 0.5 * this.get_width());
        dy = cn_mul(dy, 0.5 * this.get_length());
        var p = cn_sub(this.position, cn_add(dx, dy));
        box.enlarge_point(p);
        var p = cn_sub(this.position, cn_sub(dx, dy));
        box.enlarge_point(p);
        var p2 = cn_add(this.position, cn_add(dx, dy));
        box.enlarge_point(p2);
        var p2 = cn_add(this.position, cn_sub(dx, dy));
        box.enlarge_point(p2);
        return box;
    }

    /**
     * @param {string} image_id
     * @return string
     */
    static image_id_to_url(image_id) {
        return undefined;
    }

    get_facing_groups() {
        let facing_groups = [];
        facing_groups.push(new cn_facing_group(`Revêtement d'encadrement`, "hole", 0, CN_FACING_SUPPORTS.CEILING, [
            new cn_constant('Tableau', 'lowsill')
        ]));
        for (var side = 0; side < 2; side++) {
            let position = (side == 1) ? "extérieur" : "intérieur";
            let facing_group = new cn_facing_group(`Revêtements ${position} de baie`, "element", side, CN_FACING_SUPPORTS.OPENING, [
                new cn_constant('Dormant', 'sash')]);
            facing_groups.push(facing_group);
            if (this.opening_type.opening != "none")
                facing_group.roles.push(new cn_constant('Ouvrant', 'opening'));
            if (this.opening_type.closing != "none" && side == 1)
                facing_group.roles.push(new cn_constant('Fermeture', 'closing'));
        }

        return facing_groups;
    }

    compute_facing_count() {
        [0, 1].forEach(side => {
            for (var key in this.facings[side]) {
                if (this.facings[side][key])
                    this.facings[side][key].count++;
            }
        });
    }
}

